import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, Coffee, Dumbbell, Clock, Mic, Sparkles } from "lucide-react";
import { LiquidGlass } from "@/components/ui/liquid-glass";
// Background gradients synced with notification index
const BACKGROUND_GRADIENTS = [
  "linear-gradient(135deg, #2d1b69 0%, #11998e 100%)",
  "linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)",
  "linear-gradient(135deg, #0f3460 0%, #e94560 100%)",
  "linear-gradient(135deg, #134e5e 0%, #71b280 100%)",
  "linear-gradient(135deg, #7d3c00 0%, #b8860b 100%)",
  "linear-gradient(135deg, #3a1c71 0%, #d76d77 100%)",
];

interface Notification {
  id: number;
  name: string;
  message: string;
  time: string;
  icon: React.ReactNode;
  isDark: boolean;
  iconBg: string;
}

const notifications: Notification[] = [
  {
    id: 1,
    name: "Académie Nova",
    message: "Le webinaire commence dans 30 minutes. Cliquez pour rejoindre.",
    time: "Maintenant",
    icon: <GraduationCap className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #5e3a8c 0%, #7b4fa8 100%)", // Education - Elegant academic violet
  },
  {
    id: 2,
    name: "Café du Marché",
    message: "Offre du midi : café offert pour tout déjeuner aujourd'hui.",
    time: "10 min",
    icon: <Coffee className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #5d4037 0%, #8b6b4f 100%)", // Coffee - Warm brown
  },
  {
    id: 3,
    name: "Studio Fit+",
    message: "💪 Des places viennent de se libérer pour le cours de ce soir.",
    time: "25 min",
    icon: <Dumbbell className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #c2185b 0%, #e91e63 100%)", // Fitness - Energetic magenta
  },
  {
    id: 4,
    name: "Julie Martin - Coaching",
    message: "⏰ Rappel : votre session live démarre à 18h.",
    time: "45 min",
    icon: <Clock className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #e89bb3 0%, #d65d8c 100%)", // Coaching - Baby pink to soothing fuchsia
  },
  {
    id: 5,
    name: "CréaCast",
    message: "🎙️ Nouvel épisode disponible. Écoutez-le maintenant.",
    time: "1 h",
    icon: <Mic className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #bf360c 0%, #e65100 100%)", // Podcast - Orange/amber media
  },
  {
    id: 6,
    name: "Boutique L'Épure",
    message: "✨ Nouvelle collection en boutique. Venez découvrir.",
    time: "2 h",
    icon: <Sparkles className="w-5 h-5" />,
    isDark: true,
    iconBg: "linear-gradient(135deg, #d4a853 0%, #a68523 100%)", // Fashion - Elegant gold/yellow
  },
];

export function PhoneMockup() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % notifications.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const currentNotification = notifications[activeIndex];
  const isDarkBg = currentNotification.isDark;
  // Get current time for display
  const now = new Date();
  const hours = now.getHours().toString().padStart(2, "0");
  const minutes = now.getMinutes().toString().padStart(2, "0");
  
  // French day and date
  const days = ["Dim.", "Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam."];
  const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  const dayName = days[now.getDay()];
  const dayNum = now.getDate();
  const monthName = months[now.getMonth()];

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        setIsHovered(true);
        setIsPaused(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPaused(false);
      }}
    >
      {/* Phone Frame - iPhone 17 Pro Max Premium */}
      <div className="mockup-phone">
        {/* Side buttons */}
        <div className="mockup-phone-btn-action"></div>
        <div className="mockup-phone-btn-vol-up"></div>
        <div className="mockup-phone-btn-vol-down"></div>
        {/* Dynamic Island */}
        <div className="mockup-phone-camera"></div>
        <div className="mockup-phone-display">
          {/* Lockscreen with animated gradient background */}
          <motion.div
            className="w-full h-full flex flex-col items-center pt-12 px-4 relative overflow-hidden"
            animate={{ background: BACKGROUND_GRADIENTS[activeIndex % BACKGROUND_GRADIENTS.length] }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
          >
            {/* Status bar - iOS style */}
            <motion.div 
              className="absolute top-4 left-0 right-0 flex justify-between items-center text-[11px] z-10"
              style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif', fontWeight: 600 }}
              animate={{ color: isDarkBg ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)" }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              {/* Left side - Carrier (centered between left edge and Dynamic Island) */}
              <span className="pl-7 text-[12px]">Orange F</span>
              
              {/* Right side - Signal + WiFi + Battery — centered between Dynamic Island and right edge */}
              <div className="flex items-center gap-1.5 pr-7">
                {/* Signal bars */}
                <svg className="w-[18px] h-[14px]" viewBox="0 0 20 14" fill="currentColor">
                  <rect x="0" y="10" width="3" height="4" rx="0.5" opacity="1"/>
                  <rect x="4.5" y="7" width="3" height="7" rx="0.5" opacity="1"/>
                  <rect x="9" y="4" width="3" height="10" rx="0.5" opacity="1"/>
                  <rect x="13.5" y="0" width="3" height="14" rx="0.5" opacity="1"/>
                </svg>
                {/* WiFi */}
                <svg className="w-[16px] h-[13px]" viewBox="0 0 16 12" fill="currentColor">
                  <path d="M8 9.6a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z"/>
                  <path d="M4.7 8.3a4.7 4.7 0 0 1 6.6 0" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
                  <path d="M2.1 5.7a8.3 8.3 0 0 1 11.8 0" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
                  <path d="M0 3a11.5 11.5 0 0 1 16 0" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
                </svg>
                {/* Battery */}
                <svg className="w-[26px] h-[12px] ml-0.5" viewBox="0 0 27 13">
                  <rect x="0.5" y="0.5" width="22" height="12" rx="2.5" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.4"/>
                  <rect x="23" y="3.5" width="2.5" height="5" rx="1" fill="currentColor" opacity="0.4"/>
                  <rect x="2" y="2" width="19" height="9" rx="1.5" fill="currentColor"/>
                </svg>
              </div>
            </motion.div>

            {/* Date + Time - iOS lockscreen style */}
            <div className="mt-4 text-center" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
              {/* Date above time like real iPhone */}
              <motion.p 
                 className="text-[17px] mb-3"
                style={{ fontWeight: 600, letterSpacing: '0.01em' }}
                animate={{ color: isDarkBg ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.7)" }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                {dayName} {dayNum} {monthName}
              </motion.p>
              <motion.p 
                className="leading-none"
                style={{ fontSize: '90px', fontWeight: 300, letterSpacing: '-0.03em', lineHeight: 0.88, transform: 'scaleY(1.2)', fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif' }}
                animate={{ color: isDarkBg ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.9)" }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {hours}
                  <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '14px', transform: 'none' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: 'currentColor' }} />
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: 'currentColor' }} />
                  </span>
                  {minutes}
                </span>
              </motion.p>
            </div>

            {/* Notification - iOS Liquid Glass Style */}
            <div className="mt-8 w-full px-3">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentNotification.id}
                  initial={{ y: -20, opacity: 0, scale: 0.95 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  exit={{ y: 10, opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  <LiquidGlass
                    borderRadius="20px"
                    blurIntensity="xl"
                    glowIntensity="xs"
                    shadowIntensity="xs"
                    className="border border-white/20 bg-white/8"
                  >
                    <div className="p-3.5">
                      <div className="flex items-center gap-3">
                        {/* App Icon - Round like iOS */}
                        <div 
                          className="w-10 h-10 rounded-[12px] flex items-center justify-center flex-shrink-0 text-white"
                          style={{
                            background: currentNotification.iconBg,
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                          }}
                        >
                          {currentNotification.icon}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0 pt-0.5">
                          {/* Header row: App name + time */}
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <p 
                              className="text-[15px] tracking-tight leading-tight"
                              style={{ 
                                color: "#FFFFFF",
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
                                fontWeight: 600,
                              }}
                            >
                              {currentNotification.name}
                            </p>
                            <span 
                              className="text-[13px] flex-shrink-0"
                              style={{ 
                                color: "#D9D9D9",
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
                                fontWeight: 400,
                              }}
                            >
                              {currentNotification.time}
                            </span>
                          </div>
                          {/* Message - full display, no truncation, max 2 lines */}
                          <p 
                            className="text-[15px] leading-[1.35]"
                            style={{ 
                              color: "#FFFFFF",
                              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
                              fontWeight: 400,
                            }}
                          >
                            {currentNotification.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  </LiquidGlass>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Pagination dots */}
            <motion.div
              className="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: isHovered ? 1 : 0.3 }}
              transition={{ duration: 0.2 }}
            >
              {notifications.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === activeIndex ? "w-4" : ""
                  }`}
                  animate={{ 
                    backgroundColor: isDarkBg 
                      ? (index === activeIndex ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)") 
                      : (index === activeIndex ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.3)")
                  }}
                  whileHover={{ 
                    backgroundColor: isDarkBg ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)"
                  }}
                  transition={{ duration: 0.3 }}
                  aria-label={`Notification ${index + 1}`}
                />
              ))}
            </motion.div>

            {/* Home indicator */}
            <motion.div 
              className="absolute bottom-3 left-1/2 transform -translate-x-1/2 w-32 h-1 rounded-full"
              animate={{ backgroundColor: isDarkBg ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)" }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
